import {
  Alert,
  Button,
  Divider,
  Empty,
  Form,
  Modal,
  Select,
  Spin,
  Switch,
} from 'antd';

import { useLoyaltyChatSettingsModal } from '../hooks/useLoyaltyChatSettingsModal';

import { LoyaltyBindingConflictItem } from './LoyaltyBindingConflictItem';

import type { FC } from 'react';

import './LoyaltyChatSettingsModal.scss';

interface LoyaltyChatSettingsModalProps {
  integrationId: number;
  onCancel(): void;
  onSaved(): void | Promise<unknown>;
}

export const LoyaltyChatSettingsModal: FC<LoyaltyChatSettingsModalProps> = ({
  integrationId,
  onCancel,
  onSaved,
}) => {
  const {
    form,
    snapshot,
    isFetching,
    isSaving,
    canEdit,
    operatorOptions,
    reloadHandler,
    saveHandler,
    errorMessage,
    hasVersionConflict,
    canResolveBindings,
    conflicts,
    isConflictsLoading,
    conflictsError,
    refetchConflicts,
  } = useLoyaltyChatSettingsModal({ integrationId, onSaved });
  return (
    <Modal
      open
      title="Настройки Рекомендуй"
      className="LoyaltyChatSettingsModal"
      width={640}
      onCancel={onCancel}
      onOk={form.submit}
      okText="Сохранить"
      cancelText="Закрыть"
      okButtonProps={{
        loading: isSaving,
        disabled: !canEdit || !snapshot || isFetching || hasVersionConflict,
      }}
      cancelButtonProps={{ disabled: isSaving }}
      closable={!isSaving}
      maskClosable={!isSaving}
    >
      {errorMessage && (
        <Alert
          type="error"
          message={errorMessage}
          showIcon
          action={
            <Button
              size="small"
              onClick={reloadHandler}
              disabled={isFetching || isSaving}
            >
              {hasVersionConflict
                ? 'Загрузить актуальные настройки'
                : 'Повторить загрузку'}
            </Button>
          }
        />
      )}
      <Spin spinning={isFetching && !snapshot}>
        <Form
          form={form}
          layout="vertical"
          onFinish={saveHandler}
          disabled={!canEdit || isSaving || !snapshot}
        >
          <Form.Item
            name="chatEnabled"
            label="Чат с клиентами в Рекомендуй"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item name="operatorUserIds" label="Операторы чата">
            <Select
              mode="multiple"
              options={operatorOptions}
              placeholder="Выберите операторов"
              optionFilterProp="label"
            />
          </Form.Item>
        </Form>
      </Spin>
      {canResolveBindings && (
        <>
          <Divider orientation="left">Обращения, требующие привязки</Divider>
          {conflictsError && (
            <Alert
              type="error"
              message={conflictsError}
              action={
                <Button onClick={() => void refetchConflicts()}>
                  Повторить
                </Button>
              }
            />
          )}
          <Spin spinning={isConflictsLoading}>
            {!conflicts.length && !conflictsError ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Нет обращений, требующих проверки"
              />
            ) : (
              conflicts.map((conflict) => (
                <LoyaltyBindingConflictItem
                  key={`${conflict.conversationId}:${conflict.bindingRevision}`}
                  conflict={conflict}
                  onResolved={() => void refetchConflicts()}
                />
              ))
            )}
          </Spin>
        </>
      )}
    </Modal>
  );
};
